

































































































































































import Vue from 'vue'
import dayjs from 'dayjs'
import isEqual from 'lodash.isequal'
import Loading from '@/components/globals/basics/Loading/Loading.vue'
import SearchProgramInput from './SearchProgramInput.vue'
import ActivityModal from './ActivityModal/ActivityModal.vue'
import AreaModal from '@/components/modal/AreaModal/AreaModal.vue'
import ProgramDateModal from './ProgramDateModal/ProgramDateModal.vue'
import ProgramTimeModal, {
  TimeRange,
  Time,
} from './ProgramTimeModal/ProgramTimeModal.vue'
import SearchProgramSexLimitation from './SearchProgramSexLimitation.vue'
import SearchProgramLevel from './SearchProgramLevel.vue'
import SearchProgramCondition from './SearchProgramCondition.vue'
import { Gender, ProgramLevel } from '@/core/Programs'
import { ActivityId, Activity } from '@/core/Activities'
import { AreaId, Area, useFacilitiesMetadataStore } from '@/core/Facilities'
import snackbar from '@/tools/snackbar'

export interface FormValues {
  activityIds: ActivityId[]
  areaIds: AreaId[]
  sexLimitation: Gender[]
  level: ProgramLevel[]
  searchDates: string[]
  startTimeBefore: Time
  startTimeAfter: Time
  showFullReservations: boolean
}

const initialFormValues: FormValues = {
  activityIds: [],
  areaIds: [],
  sexLimitation: [],
  level: [],
  searchDates: [],
  startTimeBefore: { hour: '', minute: '' },
  startTimeAfter: { hour: '', minute: '' },
  showFullReservations: true,
}

const facilitiesMetadataStore = useFacilitiesMetadataStore()

export default Vue.extend({
  name: 'SearchProgramForm',
  components: {
    SearchProgramInput,
    ActivityModal,
    AreaModal,
    ProgramDateModal,
    ProgramTimeModal,
    SearchProgramSexLimitation,
    SearchProgramLevel,
    SearchProgramCondition,
    Loading,
  },
  filters: {
    formatDates: (dates: string[]) => {
      return dates.length === 0
        ? ''
        : dates.map((date) => dayjs(date).format('M月D日')).join(', ')
    },
    formatTimeRange: ({ from, to }: TimeRange) => {
      if (!from.hour && !from.minute && !to.hour && !to.minute) return ''
      return `${from.hour ? `${from.hour}:${from.minute}` : ''}〜${
        to.hour ? `${to.hour}:${to.minute}` : ''
      }`
    },
  },
  props: {
    id: {
      type: String as () => string,
      default: 'SearchProgram',
    },
    initialValues: {
      type: Object as () => FormValues,
      default: () => ({ ...initialFormValues }),
    },
    isLoadingResultCount: {
      type: Boolean,
      default: false,
    },
    resultCount: {
      type: Number as () => number | null,
      default: null,
    },
    submitLabel: {
      type: String as () => string,
      default: 'プログラム検索',
    },
  },
  data() {
    return {
      isLoading: true,
      isDirectAccess: false,
      values: {
        searchDates: [...this.initialValues.searchDates],
        activityIds: [...this.initialValues.activityIds],
        areaIds: [...this.initialValues.areaIds],
        sexLimitation: [...this.initialValues.sexLimitation],
        level: [...this.initialValues.level],
        startTimeBefore: { ...this.initialValues.startTimeBefore },
        startTimeAfter: { ...this.initialValues.startTimeAfter },
        showFullReservations: this.initialValues.showFullReservations,
      },
    }
  },
  computed: {
    activities(): Activity[] {
      return facilitiesMetadataStore.getActivities()
    },
    areas(): Area[] {
      return facilitiesMetadataStore.getAreas()
    },
    timeRangeValue(): TimeRange {
      return {
        from: this.values.startTimeBefore,
        to: this.values.startTimeAfter,
      }
    },
    activityValue(): string {
      return this.activities.length > 0
        ? this.values.activityIds
            .map((id) => this.activities.find((a) => a.id === id)?.name || '')
            .join('，')
        : ''
    },
    areaValue(): string {
      return this.areas.length > 0
        ? this.values.areaIds
            .map((id) => this.areas.find((a) => a.id === id)?.name || '')
            .join('，')
        : ''
    },
    isTouched(): boolean {
      return !isEqual(this.rawValues, { ...initialFormValues })
    },
    rawValues(): FormValues {
      return {
        searchDates: [...this.values.searchDates],
        activityIds: [...this.values.activityIds],
        areaIds: [...this.values.areaIds],
        sexLimitation: [...this.values.sexLimitation],
        level: [...this.values.level],
        startTimeBefore: { ...this.values.startTimeBefore },
        startTimeAfter: { ...this.values.startTimeAfter },
        showFullReservations: this.values.showFullReservations,
      }
    },
  },
  watch: {
    rawValues() {
      this.onChange()
    },
  },
  async created() {
    this.isLoading = true

    await this.fetchData()

    this.isLoading = false
  },
  methods: {
    async fetchData() {
      await facilitiesMetadataStore.fetch()
    },
    setSearchDates(dates: string[]) {
      this.$set(this.values, 'searchDates', dates)
    },
    setTimeRange({ from, to }: TimeRange) {
      this.$set(this.values.startTimeBefore, 'hour', from.hour)
      this.$set(this.values.startTimeBefore, 'minute', from.minute)
      this.$set(this.values.startTimeAfter, 'hour', to.hour)
      this.$set(this.values.startTimeAfter, 'minute', to.minute)
    },
    setAreas(areaIds: AreaId[]) {
      this.$set(this.values, 'areaIds', areaIds)
    },
    setActivities(activityIds: ActivityId[]) {
      this.$set(this.values, 'activityIds', activityIds)
    },
    setSexLimitation(sexLimitation: Gender[]) {
      this.$set(this.values, 'sexLimitation', sexLimitation)
    },
    setLevel(level: ProgramLevel[]) {
      this.$set(this.values, 'level', level)
    },
    setConditions(showFullReservations: boolean) {
      this.values.showFullReservations = showFullReservations
    },
    onClearAreas() {
      this.setAreas([])
    },
    onClearActivities() {
      this.setActivities([])
    },
    onClearSearchDates() {
      this.setSearchDates([])
      // 日付をクリアしたら時間もクリアしておく
      this.onClearTime()
    },
    onClearTime() {
      this.setTimeRange({
        from: { hour: '', minute: '' },
        to: { hour: '', minute: '' },
      })
    },
    onClearSexLimitation() {
      this.setSexLimitation([])
    },
    onClearLevel() {
      this.setLevel([])
    },
    onClearAll() {
      this.values = { ...initialFormValues }
      snackbar({ text: '検索条件を消去しました' })
    },
    onChange() {
      this.$emit('onChange', this.values)
    },
    onSubmit() {
      this.$emit('onSubmit', this.values)
    },
  },
})
